// src/App.jsx
import React, { useState, useEffect } from 'react';
import { Routes, Route, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setUser } from './redux/slices/authSlice';
import { useTelegramApp } from './hooks/useTelegramApp';
import { useAuth } from './contexts/AuthContext';
import { useAuthError } from './hooks/useAuthError';
import { useAppActivation } from './hooks/useAppActivation';
import { useUserLevel } from './hooks/useUserLevel';
import AppHeaderLayout from './components/AppHeaderLayout';
import GameResourcesBar from './components/ui/GameResourcesBar';
import BottomNavigation from './components/Navigation/BottomNavigation';
import LoginModal from './components/Modals/LoginModal';
import AuthDebugPanel from './components/AuthDebugPanel';
import Sidebar from './components/Layout/Sidebar';
import BuildVersion from './components/BuildVersion';
// import FakeTelegramData from './components/FakeTelegramData';
import Gallery from './Tabs/Gallery';
// GameData, CraftingPage, BattlePage, ChristmasPage — removed, redirected to /game
import MyChannels from './Tabs/MyChannels';
import Channel from './Tabs/Channel';
import MySubscriptions from './Tabs/MySubscriptions';
import Bookmarks from './Tabs/Bookmarks';
import MyCategories from './Tabs/MyCategories';
import MyChannel from './Tabs/MyChannel';
import './styles/index.css';
import Sandbox from './Tabs/Sandbox';
import CacheManagement from './Tabs/CacheManagement';
// Lazy-loaded pages
const AcceptedGreetingsPage = React.lazy(() => import('./pages/AcceptedGreetingsPage'));
const CongratulationsPage = React.lazy(() => import('./pages/CongratulationsPage'));
const ReceivedGreetingsPage = React.lazy(() => import('./pages/ReceivedGreetingsPage'));
const EditGreetingPage = React.lazy(() => import('./pages/EditGreetingPage'));
const ShortsViewer = React.lazy(() => import('./pages/ShortsViewer'));
const CreateCongratulationPage = React.lazy(() => import('./pages/CreateCongratulationPage'));
const GreetingPreviewPage = React.lazy(() => import('./pages/GreetingPreviewPage'));
const SandboxViewer = React.lazy(() => import('./pages/SandboxViewer'));
const Moderator = React.lazy(() => import('./pages/Moderator'));
const ModeratorViewer = React.lazy(() => import('./pages/ModeratorViewer'));
const AdminPanel = React.lazy(() => import('./pages/AdminPanel'));
const LevelsAdminPage = React.lazy(() => import('./pages/LevelsAdminPage'));
const WallPage = React.lazy(() => import('./pages/WallPage'));
const MyWallPage = React.lazy(() => import('./pages/MyWallPage'));
const ImageSharingTestPage = React.lazy(() => import('./pages/ImageSharingTestPage'));
const AuthorPage = React.lazy(() => import('./pages/AuthorPage'));
const AuthorsPage = React.lazy(() => import('./pages/AuthorsPage'));
const SourcePage = React.lazy(() => import('./pages/SourcePage'));
const SourcesPage = React.lazy(() => import('./pages/SourcesPage'));
// GamePage и NotificationsPage — lazy import
const GamePage = React.lazy(() => import('./pages/GamePage'));
const NotificationsPage = React.lazy(() => import('./pages/NotificationsPage'));
import FloatingShareButton from './components/FloatingShareButton';
import { httpClient } from './api/httpClient';
import AuthPage from './pages/AuthPage';

// Eruda будет инициализирована условно внутри компонента

// Режим приложения: 'game' | 'catalog' | 'full'
const APP_MODE = import.meta.env.VITE_APP_MODE || 'full';
const isGame = APP_MODE === 'game' || APP_MODE === 'full';
const isCatalog = APP_MODE === 'catalog' || APP_MODE === 'full';

// Определяем вкладки (только для каталога)
const ContentTabs = isCatalog ? {
    0: { component: Gallery, path: "/gallery" },
    1: { component: MyChannels, path: "/mychannels" },
    2: { component: MySubscriptions, path: "/mysubscriptions" },
    3: { component: Bookmarks, path: "/bookmarks" },
    4: { component: MyCategories, path: "/categories" },
    5: { component: AcceptedGreetingsPage, path: "/accepted-greetings" },
    6: { component: CongratulationsPage, path: "/congratulations" },
} : {};

function AppContent() {
    const [activeTab, setActiveTab] = useState(0);
    const [sortOption, setSortOption] = useState('popular');
    const [isAuthInProgress, setIsAuthInProgress] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const isAuthenticated = useSelector(state => state.auth.isAuthenticated);
    const authInProgress = useSelector(state => state.auth.authInProgress);
    const { isReady } = useTelegramApp();
    const { isTelegramApp, telegramUser } = useSelector((state) => state.telegram);
    const { showLoginModal, setShowLoginModal, closeLoginModal } = useAuth();
    const { hasFeature } = useUserLevel();
    
    console.log('🎨 App: Состояние инициализировано:', {
        isReady,
        isTelegramApp,
        telegramUser: !!telegramUser,
        isAuthenticated,
        location: location.pathname
    });
    
    // Используем хук для обработки ошибок аутентификации
    useAuthError();

    // Добавляем обработчик активации приложения
    useAppActivation();

    // Обработка параметра share_image для автоматического sharing
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const shareImageId = urlParams.get('share_image');
        
        if (shareImageId && window.Telegram?.WebApp) {
            console.log('🎯 Auto-sharing detected:', shareImageId);
            // Автоматически вызываем shareMessage
            setTimeout(() => {
                try {
                    window.Telegram.WebApp.shareMessage(shareImageId, (success) => {
                        if (success) {
                            console.log('✅ Auto-share successful!');
                        } else {
                            console.log('❌ Auto-share cancelled');
                        }
                    });
                } catch (error) {
                    console.error('❌ Auto-share error:', error);
                }
            }, 1000);
        }
    }, []);

    // Обработка start_param из Direct Link (t.me/bot/app?startapp=claim_xxx)
    // Используем sessionStorage чтобы обработать только один раз за сессию
    useEffect(() => {
        const startParam = window.Telegram?.WebApp?.initDataUnsafe?.start_param;
        if (!startParam || sessionStorage.getItem('start_param_handled')) return;
        sessionStorage.setItem('start_param_handled', '1');

        if (startParam.startsWith('claim_')) {
            const shareId = startParam.slice(6);
            navigate(`/stone?claim=${shareId}`, { replace: true });
        } else if (startParam.startsWith('gift_')) {
            const shareId = startParam.slice(5);
            navigate(`/game?tab=gifts&claim_gift=${shareId}`, { replace: true });
        } else if (startParam.startsWith('cat_')) {
            const categoryId = startParam.slice(4);
            navigate(`/gallery?category=${categoryId}`, { replace: true });
        } else if (startParam === 'new') {
            navigate('/gallery?filter=new', { replace: true });
        } else if (startParam.startsWith('card_')) {
            const cardId = startParam.slice(5);
            navigate(`/mychannels/9/${cardId}?from=channel`, { replace: true });
        }
    }, [navigate]);

    // Обновляем активную вкладку при изменении маршрута
    useEffect(() => {
        const path = location.pathname;
        const activeTabIndex = Object.values(ContentTabs).findIndex(tab => path.startsWith(tab.path));
        if (activeTabIndex !== -1) {
            setActiveTab(activeTabIndex);
        }
    }, [location]);

    useEffect(() => {
        const initializeAuth = async () => {
            // Предотвращаем повторную авторизацию
            if (isAuthInProgress || isAuthenticated) {
                return;
            }
            
            // Авторизация через Telegram WebApp уже происходит в AuthContext
            // Здесь только проверяем сохраненные данные пользователя для браузера
            if (!isTelegramApp) {
                const savedUser = localStorage.getItem('user');
                
                if (savedUser) {
                    try {
                        const userData = JSON.parse(savedUser);
                        dispatch(setUser(userData));
                    } catch (error) {
                        console.error('Failed to parse saved user data:', error);
                        localStorage.removeItem('user');
                    }
                }
            }
        };

        if (isReady) {
            initializeAuth();
        }
    }, [dispatch, isTelegramApp, isReady, isAuthInProgress, isAuthenticated]);

    useEffect(() => {
        // В Telegram WebApp НИКОГДА не показываем модальное окно логина
        const isTelegramEnvironment = !!(window.Telegram?.WebApp?.initData || window.Telegram?.WebApp);
        const currentPath = location.pathname;
        const isTelegramPath = currentPath.startsWith('/telegram');

        if (isTelegramEnvironment || isTelegramPath) {
            // Принудительно закрываем модалку если она каким-то образом открылась в Telegram
            if (showLoginModal) {
                setShowLoginModal(false);
            }
            return;
        }

        // Сбрасываем флаг модального окна в httpClient при навигации
        // чтобы проверка токена работала на каждой новой странице
        httpClient.resetAuthModalFlag();

        // Не показываем модалку пока авторизация ещё в процессе
        if (authInProgress) {
            return;
        }

        // Проверяем защищённые маршруты
        const protectedPaths = Object.values(ContentTabs)
            .map(tab => tab.path)
            .filter(path => path !== '/gallery');
        const additionalProtectedPaths = ['/congratulations', '/accepted-greetings'];
        const allProtectedPaths = [...protectedPaths, ...additionalProtectedPaths];
        const isProtected = allProtectedPaths.some(path => location.pathname.startsWith(path));

        // Показываем модальное окно логина только если:
        // 1. Пользователь не авторизован
        // 2. Авторизация уже завершилась (не в процессе)
        // 3. Находится на защищенной странице
        // 4. Модальное окно еще не показано через AuthContext
        // 5. Не находимся на странице авторизации
        if (!isAuthenticated && isProtected && !showLoginModal && currentPath !== '/auth') {
            setShowLoginModal(true);
        }
        // Скрываем модальное окно если пользователь авторизован
        else if (isAuthenticated && showLoginModal) {
            setShowLoginModal(false);
        }
    }, [isAuthenticated, authInProgress, showLoginModal, location.pathname]);

    // Условная инициализация Eruda для отладки
    useEffect(() => {
        if (hasFeature('BASE_DEBUG')) {
            import('eruda').then(({ default: eruda }) => {
                eruda.init();
                console.log('🔧 Eruda инициализирована для отладки (BASE_DEBUG enabled)');
            }).catch(error => {
                console.error('❌ Ошибка инициализации Eruda:', error);
            });
        } else {
            console.log('🔧 Eruda не инициализирована (BASE_DEBUG disabled)');
        }
    }, [hasFeature]);

    // Показываем спиннер загрузки пока приложение не готово
    // В Telegram среде также ждём завершения авторизации
    const isTelegramEnvironment = !!window.Telegram?.WebApp;
    const shouldShowLoader = !isReady || (isTelegramEnvironment && authInProgress);

    if (shouldShowLoader) {
        return (
            <div className="h-[100dvh] flex items-center justify-center bg-gray-100 dark:bg-gray-800">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600 dark:text-gray-400">Загрузка...</p>
                </div>
            </div>
        );
    }

    // Определяем активный компонент
    const ContentComponent = ContentTabs[activeTab]?.component || Gallery;

    return (
        <div className="app">
            <Sidebar setActiveTab={setActiveTab} />
            <GameResourcesBar />
            
            <main className={`main-content ${!hasFeature('BASE_GAME') ? 'no-game-bar' : ''} ${!hasFeature('BASE_BOTTOMPANEL') || (APP_MODE === 'catalog') ? 'no-bottom-panel' : ''}`}>
                <React.Suspense fallback={<div className="h-full flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" /></div>}>
                <Routes>
                    {/* Shared routes */}
                    <Route path="/auth" element={<AuthPage />} />

                    {/* Game routes */}
                    {isGame && (
                        <>
                            <Route path="/game" element={<GamePage />} />
                            <Route path="/notifications" element={<NotificationsPage />} />
                        </>
                    )}

                    {/* Catalog routes */}
                    {isCatalog && (
                        <>
                            <Route path="/" element={<ContentComponent sortOption={sortOption} setSortOption={setSortOption} />} />
                            {Object.values(ContentTabs).map((tab, idx) => (
                                <Route key={tab.path} path={tab.path} element={<tab.component sortOption={sortOption} setSortOption={setSortOption} />} />
                            ))}
                            <Route path="/mychannels/:id" element={<Channel />} />
                            <Route path="/mychannels/:channelId/:postcardId" element={<ShortsViewer />} />
                            <Route path="/bookmarks/:postcardId" element={<ShortsViewer />} />
                            <Route path="/mychannels/:channelId/:postcardId/create-congratulation" element={<CreateCongratulationPage />} />
                            <Route path="/greeting/:greetingId" element={<GreetingPreviewPage />} />
                            <Route path="/edit-greeting/:greetingId" element={<EditGreetingPage />} />
                            <Route path="/received-greetings" element={<ReceivedGreetingsPage />} />
                            <Route path="/stone" element={<MyWallPage />} />
                            <Route path="/stone/view/:postcardId" element={<ShortsViewer />} />
                            <Route path="/stone/:userId" element={<WallPage />} />
                            <Route path="/cache-management" element={<CacheManagement />} />
                            <Route path="/my-channel" element={<MyChannel />} />
                            <Route path="/sandbox" element={<Sandbox />} />
                            <Route path="/sandbox/:postcardId" element={<SandboxViewer />} />
                            <Route path="/moderator" element={<Moderator />} />
                            <Route path="/moderator/:postcardId" element={<ModeratorViewer />} />
                            <Route path="/image-sharing-test" element={<ImageSharingTestPage />} />
                            <Route path="/authors" element={<AuthorsPage />} />
                            <Route path="/authors/:authorId" element={<AuthorPage />} />
                            <Route path="/authors/:authorId/view/:postcardId" element={<ShortsViewer />} />
                            <Route path="/sources/:sourceType" element={<SourcesPage />} />
                            <Route path="/source/:sourceId" element={<SourcePage />} />
                            <Route path="/source/:sourceId/view/:postcardId" element={<ShortsViewer />} />
                        </>
                    )}

                    {/* Admin routes (always available) */}
                    <Route path="/admin" element={<AdminPanel />} />
                    <Route path="/admin/levels" element={<LevelsAdminPage />} />

                    {/* Legacy redirects */}
                    <Route path="/game-data" element={<Navigate to={isGame ? '/game' : '/gallery'} replace />} />
                    <Route path="/crafting" element={<Navigate to={isGame ? '/game' : '/gallery'} replace />} />
                    <Route path="/battle" element={<Navigate to={isGame ? '/game' : '/gallery'} replace />} />
                    <Route path="/christmas" element={<Navigate to={isGame ? '/game' : '/gallery'} replace />} />

                    {/* Default redirect based on mode */}
                    <Route path="/" element={
                        <Navigate to={APP_MODE === 'game' ? '/game' : '/gallery'} replace />
                    } />
                </Routes>
                </React.Suspense>
            </main>
            
            {hasFeature('BASE_BOTTOMPANEL') && (
                <BottomNavigation activeTab={activeTab} />
            )}
            
            {/* Плавающая кнопка для тестирования отправки изображений */}
            {hasFeature('SOCIAL_SHARE') && <FloatingShareButton />}
            
            {/* Не отображаем LoginModal внутри Telegram среды */}
            {showLoginModal && !window.Telegram?.WebApp && (
                <LoginModal
                    isOpen={showLoginModal}
                    onClose={closeLoginModal}
                />
            )}
            
            {/* Отладочная панель авторизации */}
            {/* 
            🔐 AUTH DEBUG PANEL - ЗАКОММЕНТИРОВАНО
            Для включения раскомментируйте строку ниже:
            <AuthDebugPanel />
            */}
            {/* <AuthDebugPanel /> */}
            { <BuildVersion /> }
            {/* <FakeTelegramData /> */}
        </div>
    );
}

function App() {
    const location = useLocation();

    // В Telegram среде НИКОГДА не показываем страницу авторизации
    const isTelegramEnvironment = !!window.Telegram?.WebApp;

    // Если это страница авторизации и мы НЕ в Telegram - рендерим ТОЛЬКО её
    if (location.pathname === '/auth' && !isTelegramEnvironment) {
        return <AuthPage />;
    }

    // Иначе рендерим основное приложение
    return <AppContent />;
}

export default App;
